import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ladeDateiHoch, ladeDateiHerunter, loescheDatei, verschiebeDatei } from 'modules/wissen/features/wissenSlice';
import { formatiereGroesse, ordnerMitTiefe } from 'modules/wissen/components/wissenHelpers';

const MAX_BYTES = 25 * 1024 * 1024; // 25 MB pro Datei

const datum = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE') : '');

/**
 * Verwaltung freier (nicht an Notizen angehaengter) verschluesselter Dateien:
 * Hochladen (im Browser verschluesselt), Herunterladen (im Browser entschluesselt),
 * Loeschen und Verschieben zwischen Ordnern.
 */
export default function DateienPanel({ dateien, ordner, aktuellerOrdnerId }) {
  const dispatch = useDispatch();
  const eingabe = useRef(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState(null);

  // Anhaenge von Notizen erscheinen nur in ihrer Notiz, nicht im Dateien-Bereich.
  const freieDateien = dateien.filter((d) => !d.notizUuid);
  const ordnerOptionen = ordnerMitTiefe(ordner);

  const hochladen = async (event) => {
    const dateiListe = Array.from(event.target.files || []);
    event.target.value = '';
    setFehler(null);
    for (const file of dateiListe) {
      if (file.size > MAX_BYTES) {
        setFehler(`„${file.name}" ist größer als ${formatiereGroesse(MAX_BYTES)} und wurde übersprungen.`);
        continue;
      }
      setLaedt(true);
      try {
        await dispatch(ladeDateiHoch({ file, ordnerId: aktuellerOrdnerId ?? null })).unwrap();
      } catch (ex) {
        setFehler(`Hochladen von „${file.name}" fehlgeschlagen.`);
      }
    }
    setLaedt(false);
  };

  const loeschen = (d) => {
    if (window.confirm(`Datei „${d.name}" löschen?`)) {
      dispatch(loescheDatei(d.uuid));
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => eingabe.current?.click()}
          disabled={laedt}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500 disabled:opacity-50"
        >
          {laedt ? 'Verschlüssle & lade hoch…' : '+ Datei hochladen'}
        </button>
        <input ref={eingabe} type="file" multiple onChange={hochladen} className="hidden" />
        <span className="text-xs text-ink-muted">Max. {formatiereGroesse(MAX_BYTES)} pro Datei · verschlüsselt gespeichert</span>
      </div>

      {fehler && <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{fehler}</p>}

      {freieDateien.length === 0 ? (
        <p className="rounded-xl border border-dashed border-line bg-paper-soft p-8 text-center text-ink-muted">
          Keine freien Dateien vorhanden.
        </p>
      ) : (
        <ul className="divide-y divide-line rounded-xl border border-line bg-paper">
          {freieDateien.map((d) => (
            <li key={d.uuid} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <span className="text-xl" aria-hidden="true">📄</span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-ink">{d.name}</p>
                <p className="text-xs text-ink-muted">{formatiereGroesse(d.groesseOriginal)} · {datum(d.createdAt)}</p>
              </div>
              <select
                value={d.ordnerId ?? ''}
                onChange={(e) => dispatch(verschiebeDatei({ uuid: d.uuid, ordnerId: e.target.value === '' ? null : Number(e.target.value) }))}
                className="rounded-lg border border-line bg-paper px-2 py-1 text-xs text-ink focus:border-brand-600 focus:outline-none"
              >
                <option value="">Kein Ordner</option>
                {ordnerOptionen.map((o) => (
                  <option key={o.uuid} value={o.id}>{' '.repeat(o.tiefe * 2)}{o.name}</option>
                ))}
              </select>
              <button type="button" onClick={() => dispatch(ladeDateiHerunter(d))} className="rounded-lg px-3 py-1.5 text-sm text-brand-600 hover:bg-brand-600/10">
                Herunterladen
              </button>
              <button type="button" onClick={() => loeschen(d)} className="rounded-lg px-3 py-1.5 text-sm text-red-600 hover:bg-red-50">
                Löschen
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
